import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { get, post } from 'axios';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { Alert, Button, Link } from 'xm-spark';
import { useDebounceFn } from 'hooks/useDebounceFn';
import { useGlobalState } from 'hooks/useGlobalState';
import { useRecaptcha } from 'hooks/useRecaptcha';
import { requiredErrorMsg, unexpectedServerErrorMsg } from 'utils/message';

import InputField from './InputField';
import CheckBoxField from './CheckboxField';
import styles from './SignupForm.module.scss';
import { getDataForMarketoForm, isXmattersEmail, trimParams } from '../util';

const RECAPTCHA_CONTAINER_ID = 'xm_recaptcha';
export const invalidNameCharsMsg =
  'Cannot contain special characters (e.g., %&<>#!)';
export const invalidNameCharsRegex = /^((?!%|&|<|>|#|!).)*$/;
export const invalidCompanyNameCharsMsg =
  'Cannot contain special characters (e.g., %&<>#!.,)';
export const invalidCompanyNameCharsRegex = /^((?!%|&|<|>|#|!|\.|,).)*$/;
export const emailValidationErrMsg = 'Must be a valid email.';
export const companyNameExistedMsg = 'The company name is not available.';
export const emailExistedMsg = (
  <>
    This email is already in use. Use a different email or contact
    <Link
      className={styles.supportLink}
      href="https://support.xmatters.com/hc/en-us/requests/new"
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      {' '}
      Support{' '}
    </Link>
    for help logging into your instance.
  </>
);

const ZOOMINFO_CONTAINER_ID = 'zi-return-fields';
// SIGNUP_FORM_ID value must match what is configured in ZoomInfo FormComplete for the "provisioning-customer-category" form
const SIGNUP_FORM_ID = 'provision-signup';

const postForm = (formData) =>
  post(
    `xm/free/shared/api/signup?g-recaptcha-response=${formData.catchaResponse}`,
    {
      ...pick(formData, [
        'firstName',
        'lastName',
        'companyName',
        'region',
        'fcFullResponse',
        'nbStatus',
      ]),
      emailAddress: formData.emailAddress.toLowerCase(),
    },
  );
export default function SignupForm({ marketoScriptReady }) {
  const {
    clearErrors,
    register,
    handleSubmit,
    control,
    errors,
    setError,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm();

  const isDevModeRef = useRef(
    (window.location.href.indexOf('devMode=true') > 0 ||
      window.location.href.indexOf('dev.xmatters.com') > 0 ||
      window.location.href.indexOf('tst.xmatters.com') > 0) &&
      window.location.href.indexOf('devMode=false') === -1,
  );

  const marketoStatusRef = useRef(marketoScriptReady);
  const history = useHistory();
  const [{ signup }, dispatch] = useGlobalState();

  const successCallback = (response) =>
    handleSubmit((data) => onSubmit({ ...data, catchaResponse: response }))();
  const { recaptchaLoaded, recaptchaWidget } = useRecaptcha(
    RECAPTCHA_CONTAINER_ID,
    successCallback,
  );

  const { error: signupError } = signup;
  const region = watch('region');
  const companyName = watch('companyName');
  const [validating, setValidating] = useState(false);
  const canSubmit =
    !isSubmitting && !validating && isEmpty(errors) && recaptchaLoaded;
  const [apiError, setApiError] = useState(null);
  const clearApiError = () => setApiError(null);

  const validateCompany = (companyName, region) =>
    get(
      `/xm/free/shared/api/companyName/${encodeURIComponent(
        companyName,
      )}?region=${region}`,
    )
      .then(() => {
        setError('companyName', {
          type: 'duplicate',
          message: companyNameExistedMsg,
        });
      })
      .catch((error) => {
        if (error?.response?.status !== 404) {
          setApiError(error?.json?.message || unexpectedServerErrorMsg);
        } else {
          clearApiError();
        }
      })
      .finally(() => setValidating(false));

  const debouncedValidateCompany = useDebounceFn(validateCompany, 500);

  useEffect(() => {
    if (signupError) {
      setApiError(unexpectedServerErrorMsg);
    }
  }, [signupError, setApiError]);

  useEffect(() => {
    if (companyName?.trim() && region) {
      setValidating(true);
      clearErrors('companyName');
      debouncedValidateCompany(companyName, region);
    }
  }, [companyName, region, debouncedValidateCompany, clearErrors]);

  useEffect(() => {
    // keep the updated marketoScriptReady value in a ref so that onSubmit function can avoid stale prop
    marketoStatusRef.current = marketoScriptReady;
  }, [marketoScriptReady]);

  useEffect(() => {
    dispatch({
      type: 'SET_WAITING_SCREEN_VISIBLE',
      payload: false,
    });
  }, [dispatch]);

  useEffect(() => {
    window._zi = {
      formId: 'aWtTJXQX6CzK3VRYCT9g',
      development: isDevModeRef.current,
    };
    window._zi.callbacks = {
      onMatch: function (data) {
        const nbStatus = document
          .getElementsByName('emailAddress')[0]
          .getAttribute('data-zi-neverbounce-status');
        document.getElementById('nbStatus').value = nbStatus;
        document.getElementById('fcFullResponse').value = JSON.stringify(data);
      },
    };
    const zoomInfoScriptObject = document.createElement('script');
    zoomInfoScriptObject.type = 'text/javascript';
    zoomInfoScriptObject.async = true;
    zoomInfoScriptObject.src = 'https://ws-assets.zoominfo.com/formcomplete.js';
    const scriptObject = document.getElementsByTagName('script')[0];
    scriptObject.parentNode.insertBefore(zoomInfoScriptObject, scriptObject);
  }, []);

  const onSubmit = (data) => {
    clearApiError();
    data = trimParams(data, ['firstName', 'lastName', 'companyName']);
    return postForm(data)
      .then((res) => {
        dispatch({
          type: 'SIGNUP_BEGIN',
          payload: res.data,
        });
        if (
          !isDevModeRef.current &&
          marketoStatusRef.current &&
          !isXmattersEmail(data.emailAddress)
        ) {
          const marketoForm = window.MktoForms2.allForms()[0];
          marketoForm.addHiddenFields(getDataForMarketoForm(data));
          marketoForm.submit();
        }
        history.push(`/onboarding`);
      })
      .catch((error) => {
        const errorCode = error?.response?.status;
        if (errorCode === 409) {
          setError('emailAddress', {
            type: 'duplicate',
            message: emailExistedMsg,
          });
        } else if (errorCode >= 500) {
          setApiError(unexpectedServerErrorMsg);
        } else
          setApiError(
            error?.response?.data?.message || unexpectedServerErrorMsg,
          );
      });
  };

  const executeCaptcha = (e) => {
    e.preventDefault();
    if (recaptchaWidget !== null) {
      window.grecaptcha.reset(recaptchaWidget);
      window.grecaptcha.execute(recaptchaWidget);
    }
  };

  const setEmail = (e) => {
    let emailStr = e.target.value.trim();
    setValue('emailAddress', emailStr);
  };

  return (
    <form
      id={SIGNUP_FORM_ID}
      className={styles.signupForm}
      onSubmit={executeCaptcha}
    >
      {apiError && (
        <Alert
          error
          role="alert"
          className={styles.alertBox}
          data-testid="signupForm_alert"
        >
          <span>{apiError}</span>
        </Alert>
      )}
      <h3 className={styles.h2}>No credit card required</h3>
      <InputField
        autoComplete="given-name"
        error={errors.firstName}
        fieldName="firstName"
        inputRef={register({
          pattern: {
            value: invalidNameCharsRegex,
            message: invalidNameCharsMsg,
          },
          validate: (value) => !!value.trim() || requiredErrorMsg,
        })}
        labelText="First Name"
        maxLength={100}
        placeholder="Morgan"
        testid="signupForm_firstName"
        width="halfWidth"
      />
      <InputField
        autoComplete="family-name"
        error={errors.lastName}
        fieldName="lastName"
        inputRef={register({
          pattern: {
            value: invalidNameCharsRegex,
            message: invalidNameCharsMsg,
          },
          validate: (value) => !!value.trim() || requiredErrorMsg,
        })}
        labelText="Last Name"
        maxLength={100}
        placeholder="Smith"
        testid="signupForm_lastName"
        width="halfWidth"
      />
      <InputField
        autoComplete="email"
        error={errors.emailAddress}
        fieldName="emailAddress"
        inputRef={register({
          required: requiredErrorMsg,
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,16}$/i,
            message: emailValidationErrMsg,
          },
        })}
        labelText="Company Email"
        maxLength={254}
        placeholder="Your free URL will be delivered here"
        testid="signupForm_emailAddress"
        onChange={setEmail}
      />
      <InputField
        autoComplete="organization"
        error={errors.companyName}
        fieldName="companyName"
        inputRef={register({
          pattern: {
            value: invalidCompanyNameCharsRegex,
            message: invalidCompanyNameCharsMsg,
          },
          validate: (value) => !!value.trim() || requiredErrorMsg,
        })}
        labelText="Company"
        maxLength={100}
        placeholder="eg: Acme Corp"
        testid="signupForm_companyName"
        validating={validating}
      />
      <div className={classnames(styles.topLabel, styles.fullWidth)}>
        <label htmlFor="region">Region</label>
        <select
          name="region"
          className={classnames(styles.field, styles.selectField)}
          defaultValue=""
          ref={register({ required: requiredErrorMsg })}
          required
          data-testid="signupForm_region"
        >
          <option value="" disabled>
            Select
          </option>
          <option value="NA">Americas</option>
          <option value="EMEA">EMEA</option>
          <option value="APAC">APAC</option>
        </select>
      </div>

      <div className={classnames(styles.textWrap, styles.fullWidth)}>
        <span>
          Your region helps determine the best location to host your instance.
        </span>
      </div>

      <div className={classnames(styles.checkboxContainer, styles.fullWidth)}>
        <Controller
          name="consent"
          control={control}
          defaultValue={false}
          rules={{ required: requiredErrorMsg }}
          render={(props) => (
            <CheckBoxField {...props} error={!!errors.consent} />
          )}
        />
        {errors.consent && (
          <span role="alert" className={styles.errorText}>
            {errors.consent.message}
          </span>
        )}
      </div>

      <div id={ZOOMINFO_CONTAINER_ID}>
        <input type="hidden" name="jobTitle" />
        <input type="hidden" name="ziCompanyId" />
        <input type="hidden" name="companyWebsite" />
        <input type="hidden" name="companyPhone" />
        <input type="hidden" name="annualRevenue" />
        <input type="hidden" name="employeeCount" />
        <input type="hidden" name="city" />
        <input type="hidden" name="state" />
        <input type="hidden" name="country" />
        <input type="hidden" name="zipCode" />
        <input type="hidden" name="industry" />
        <input type="hidden" name="department" />
        <input type="hidden" name="jobFunction" />
        <input type="hidden" name="managementLevel" />
        <input type="hidden" name="ziContactId" />
        <input type="hidden" name="ziContactFirst" />
        <input type="hidden" name="ziContactLast" />
        <input type="hidden" name="ziCompany" />
        <input type="hidden" name="ziCAS" />
        <input type="hidden" name="ziCAG" />
        <input
          type="hidden"
          ref={register()}
          id="fcFullResponse"
          name="fcFullResponse"
        />
        <input type="hidden" ref={register()} id="nbStatus" name="nbStatus" />
      </div>

      <div className={styles.fullWidth}>
        <Button
          className={styles.button}
          type="submit"
          color="transparent"
          disabled={!canSubmit}
          data-testid="signupForm_btn"
        >
          {isSubmitting ? 'Please wait' : 'Get started'}
        </Button>
      </div>

      <div id={RECAPTCHA_CONTAINER_ID} />
    </form>
  );
}

SignupForm.propTypes = {
  marketoScriptReady: PropTypes.bool.isRequired,
};
